import { BaseResourceStore } from "_common/resources/BaseResourceStore";
import { fetchUtils } from "_common/_utils/fetchUtils";
import i18next from "i18next";
import { TCommunitiesListingMdl, TCommunityLocalized } from "admin/communities/_models/CommunitiesMdl";
import { action, computed, observable } from "mobx";
import { TFilter } from "admin/_common/filters/TFilter";
import { FILTERS_KEYS } from "_configs/filtersConfig";
import { TFilterType } from "admin/_common/resources/ResourceFilterMdl";
import { LoadingStateMdl } from "_common/loaders/_models/LoadingStateMdl";
import { TMapCoordinates, TMapLocation } from "maps/Map";
import { LatLng } from "leaflet";
import { DEFAULT_LOCATION, DEFAULT_MAP_COORDINATES, DEFAULT_ZOOM } from "_common/_utils/searchUtils";
import _ from "lodash";
import { FILTER_EXCLUDED_FOR_FILTERS_BTN_SAVED_SEARCH } from "properties/_models/PropertyMdl";

export class CommunitiesStore extends BaseResourceStore<TCommunitiesListingMdl> {
    @observable items: TCommunitiesListingMdl[] = [];
    @observable communitiesSearchState: LoadingStateMdl<TCommunitiesListingMdl[]> = new LoadingStateMdl<
        TCommunitiesListingMdl[]
    >();
    @observable communitiesSearch: TCommunitiesListingMdl[] = [];
    @observable searchParams: {
        autocomplete: google.maps.places.Autocomplete | null;
        location: TMapLocation | LatLng;
        zoom: number;
        mapCoordinates: TMapCoordinates;
    } = {
        autocomplete: null,
        location: DEFAULT_LOCATION,
        zoom: DEFAULT_ZOOM,
        mapCoordinates: DEFAULT_MAP_COORDINATES,
    };
    @observable mapCommunityIdSelected: string | undefined = undefined;

    abortControllers = {
        deepList: {
            ctrl: new AbortController(),
            state: new LoadingStateMdl(),
        },
        list: {
            ctrl: new AbortController(),
            state: new LoadingStateMdl(),
        },
    };

    setMapCommunitySelected = _.debounce((communityId?: string) => {
        this.mapCommunityIdSelected = communityId;
    }, 400);

    constructor() {
        super("communities");
        this.onInit();
    }

    @computed get communitiesSearchResults() {
        return this.communitiesSearch;
    }

    @computed get getArrCache() {
        if (CommunitiesStore.hasUserFilteredCommunities(this.listsStores["communities"].filters)) {
            return this.items;
        }
        return Object.values(this.cache);
    }

    static hasUserFilteredCommunities(filters: TFilter[]) {
        return this.numberOfFiltersUserHasSetByHimself(filters).length > 0;
    }

    static numberOfFiltersUserHasSetByHimself(filters: TFilter[]) {
        return filters.filter(
            (filter) =>
                !Object.values(FILTER_EXCLUDED_FOR_FILTERS_BTN_SAVED_SEARCH).includes(
                    filter.id as FILTER_EXCLUDED_FOR_FILTERS_BTN_SAVED_SEARCH,
                ),
        );
    }

    @action resetItems() {
        this.items = [];
    }

    @action setLocation(location?: TMapLocation) {
        if (location) {
            this.searchParams.location = location;
        } else {
            this.searchParams.location = DEFAULT_LOCATION;
        }
    }

    @action setBounds(bounds?: TMapCoordinates) {
        if (bounds) {
            this.searchParams.mapCoordinates = bounds;
        } else {
            this.searchParams.location = DEFAULT_LOCATION;
        }
    }

    @action
    setZoomForMobile() {
        this.searchParams.zoom = 2;
    }

    @action
    putItemInCache(item: TCommunitiesListingMdl) {
        super.putItemInCache(item);
        const itemIdex = this.items.findIndex((_item) => _item._id === item._id);
        if (itemIdex === -1) this.items.push(item);
    }

    list(
        offset = 0,
        limit?: number,
        _listId?: string,
        sort?: { [key: string]: number },
        filters?: TFilter[],
        countForStats = true,
    ) {
        if (this.abortControllers.list.state.isLoading) {
            this.abortControllers.list.ctrl.abort();
        }
        this.abortControllers.list.ctrl = new AbortController();
        this.abortControllers.list.state.startLoading();
        const sortParam = sort ? `&sort=${JSON.stringify(sort)}` : "";
        if (!filters) filters = [];
        const filtersParam = filters.length > 0 ? `&filters=${JSON.stringify(filters)}` : "";
        const url = `${this.apiPath}/listing?offset=${offset}&limit=${limit}${sortParam}${filtersParam}&lang=${
            i18next.language
        }&browser=${countForStats && __BROWSER__}`;
        const promise = fetchUtils
            .get<{ count: number; items: TCommunitiesListingMdl[] }>(url, {
                signal: this.abortControllers.list.ctrl.signal,
            })
            .then(
                ({ data: { count, items } }) => {
                    this.abortControllers.list.state.setSuccess();
                    return {
                        count,
                        items: items.map((item) => {
                            const reformattedItem = this.reformatItem(item);
                            this.putItemInCache(reformattedItem);
                            return reformattedItem;
                        }),
                    };
                },
                (e) => this.abortControllers.list.state.setError(e),
            );
        return promise;
    }

    deepList(filters?: TFilter[]) {
        if (this.abortControllers.deepList.state.isLoading) {
            this.abortControllers.deepList.ctrl.abort();
        }
        this.abortControllers.deepList.ctrl = new AbortController();
        this.abortControllers.deepList.state.startLoading();
        if (!filters) filters = [];
        const filtersParam = filters.length > 0 ? `&filters=${JSON.stringify(filters)}` : "";
        const url = `${this.apiPath}/deepListing?${filtersParam}&lang=${i18next.language}`;
        const promise = fetchUtils
            .get<{ count: number; items: TCommunitiesListingMdl[] }>(url, {
                signal: this.abortControllers.deepList.ctrl.signal,
            })
            .then(
                ({ data: { count, items } }) => {
                    this.abortControllers.deepList.state.setSuccess();
                    return {
                        count,
                        items: items.map((item) => {
                            const reformattedItem = this.reformatItem(item);
                            this.putItemInCache(reformattedItem);
                            return reformattedItem;
                        }),
                    };
                },
                (e) => this.abortControllers.deepList.state.setError(e),
            );
        return promise;
    }

    fetchCommunitiesForSearch(value?: string, limit = 5) {
        if (!this.communitiesSearchState.isLoading) {
            this.communitiesSearchState.startLoading();
            const filters: TFilter[] = [];
            value ? filters.push({ id: FILTERS_KEYS.TITLE_EN, type: TFilterType.STRING, value }) : "";
            const filtersParam = filters.length > 0 ? `&filters=${JSON.stringify(filters)}` : "";
            const url = `${this.apiPath}/listing?limit=${limit}&lang=${i18next.language}${filtersParam}&searchBar=true`;
            return fetchUtils.get<{ items: TCommunitiesListingMdl[] }>(url).then(({ data }) => {
                this.communitiesSearch = data.items;
                this.communitiesSearchState.setSuccess(data.items);
            });
        }
    }

    getByAliasUrl(urlAlias: string, wantedLanguage?: string) {
        if (!wantedLanguage) {
            for (const communityId of Object.keys(this.cache)) {
                if (this.cache[communityId]?.localized.urlAlias === urlAlias) {
                    return this.cache[communityId];
                }
            }
        }

        const url = `${this.apiPath}/urlAlias/${urlAlias}/${wantedLanguage ?? i18next.language}`;
        return fetchUtils
            .get<TCommunitiesListingMdl>(url)
            .then(({ data }) => data)
            .catch((e) => console.error(e));
    }

    getCommunitiesOfGeoZone(geoZoneId?: string, lang: string = i18next.language) {
        const geoZoneParams = geoZoneId ? `/${geoZoneId}` : "";
        const url = `${this.apiPath}/getByGeoZone/${lang}${geoZoneParams}`;
        return fetchUtils.get<{ _id: string; localized: TCommunityLocalized }[]>(url).then(
            action(({ data }) => {
                return data;
            }),
        );
    }
    protected onReset() {
        this.communitiesSearch = [];
        this.communitiesSearchState = new LoadingStateMdl<TCommunitiesListingMdl[]>();
        super.onReset();
    }
}

const communitiesStore = new CommunitiesStore();
export { communitiesStore };
